/**
 * The `doc/v1` template document, as the editor holds it (D10).
 *
 * THE SERVER'S GRAMMAR, AND NOTHING ELSE. `automation_template_doc` (D4) is the
 * one place that says what a template is; this module edits exactly that
 * grammar and produces nothing it would refuse:
 *
 *   body     doc → 1+ blocks: paragraph | bullet_list | ordered_list
 *            list → list_item → 1+ paragraphs (no nested lists)
 *            inline: text | variable | hard_break; marks strong, emphasis,
 *            underline (canonical order)
 *   subject  doc → exactly one paragraph of text and variables — no marks,
 *            no line break, no list
 *
 * No HTML string is ever the source, no Markdown, no `{{ … }}` syntax: a
 * placeholder is a `variable` node naming a key, and the only keys the editor
 * can place are the ones the server's catalogue offered for this automation.
 * Braces typed into a text are four characters of text, nothing more.
 *
 * THE EDITING MODEL. A paragraph is a list of SEGMENTS:
 *
 *   { kind: "text", text, marks }      a run of text with one set of marks;
 *                                      "\n" in it is a `hard_break`
 *   { kind: "variable", key, marks }   one placeholder, an atomic unit
 *
 * Every paragraph keeps a text segment before, between and after its
 * placeholders (possibly empty), so the caret always has somewhere to go, and
 * a placeholder is only ever inserted into, or removed from between, text.
 * Segment ids exist for React and for focus; they never reach the server.
 *
 * SERIALISATION IS THE NORMAL FORM. Empty texts are dropped, adjacent texts
 * with the same marks are joined, marks come in canonical order and an empty
 * `marks` is left out — the shape the server returns after validating. Two
 * models that mean the same letter therefore serialise to the same bytes,
 * which is what "dirty" and "is this preview current" compare.
 *
 * STRUCTURAL BLOCKS ARE PLACES, AND THE SERVER NAMES THEM (D12a). A body may
 * hold a block the server fills at send time — the reminder's list of
 * outstanding obligations (`{ type: "reminder_requirements" }`). Which ones a
 * body holds comes with the template (`structural_blocks`), never from the
 * automation's key: each named block is in the body exactly once, it carries
 * nothing but its type, and the editor can neither write into it, turn it into
 * text, remove it nor add a second one. A new draft is born with each of them
 * between two empty paragraphs, so a draft is never missing one.
 *
 * LIMITS COME FROM THE SERVER. `limits` (bytes, body nodes, subject characters)
 * arrive with the template; the checks below take them as arguments and invent
 * none. The server still validates everything — these only let the editor say
 * what is wrong before a request is spent on it.
 */

export const DOC_FORMAT = "doc/v1";
export const MARKS = ["strong", "emphasis", "underline"];
export const BLOCK_TYPES = ["paragraph", "bullet_list", "ordered_list"];
/** The model's type for a structural block; its node type is `block.node`. */
export const STRUCTURAL = "structural";
const LIST_TYPES = ["bullet_list", "ordered_list"];

let lastId = 0;
function nextId(prefix) {
  lastId += 1;
  return `${prefix}-${lastId}`;
}

function canonicalMarks(marks) {
  const set = new Set(Array.isArray(marks) ? marks : []);
  return MARKS.filter((mark) => set.has(mark));
}

function sameMarks(a, b) {
  return canonicalMarks(a).join("|") === canonicalMarks(b).join("|");
}

function textSegment(text = "", marks = []) {
  return { id: nextId("t"), kind: "text", text, marks: canonicalMarks(marks) };
}

function variableSegment(key, marks = []) {
  return { id: nextId("v"), kind: "variable", key, marks: canonicalMarks(marks) };
}

/**
 * What a text field may hold. Line endings become "\n" (a `hard_break` in a
 * body, a space in a subject); a tab becomes a space; every other control
 * character — which the server refuses — is dropped.
 */
export function sanitizeText(value, { singleLine = false } = {}) {
  let text = String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(/[\u2028\u2029]/g, "\n")
    .replace(/\t/g, " ")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/g, "");
  if (singleLine) text = text.replace(/\n/g, " ");
  return text;
}

/**
 * A text segment before, between and after the placeholders — never two
 * placeholders without a caret position between them.
 */
export function ensureEditable(segments) {
  const out = [];
  for (const segment of segments) {
    const previous = out[out.length - 1];
    if (segment.kind === "variable" && (!previous || previous.kind === "variable")) {
      out.push(textSegment(""));
    }
    out.push(segment);
  }
  if (!out.length || out[out.length - 1].kind === "variable") {
    out.push(textSegment(""));
  }
  return out;
}

/** Join neighbouring texts that carry the same marks (keeps the first id). */
function mergeTexts(segments) {
  const out = [];
  for (const segment of segments) {
    const previous = out[out.length - 1];
    if (
      segment.kind === "text" &&
      previous &&
      previous.kind === "text" &&
      (sameMarks(previous.marks, segment.marks) || previous.text === "" || segment.text === "")
    ) {
      const keepMarks = previous.text === "" ? segment.marks : previous.marks;
      out[out.length - 1] = {
        ...previous,
        text: previous.text + segment.text,
        marks: keepMarks,
      };
      continue;
    }
    out.push(segment);
  }
  return out;
}

// ── doc → model ─────────────────────────────────────────────────────────

/** A node's marks (`[{type: "strong"}, …]`) as the model's mark names. */
function marksOfNode(node) {
  return Array.isArray(node?.marks) ? node.marks.map((mark) => mark?.type) : [];
}

function paragraphFromNode(node) {
  const segments = [];
  for (const inline of Array.isArray(node?.content) ? node.content : []) {
    const previous = segments[segments.length - 1];
    if (inline?.type === "text" && typeof inline.text === "string") {
      const marks = marksOfNode(inline);
      if (previous && previous.kind === "text" && sameMarks(previous.marks, marks)) {
        previous.text += inline.text;
      } else {
        segments.push(textSegment(inline.text, marks));
      }
    } else if (inline?.type === "hard_break") {
      if (previous && previous.kind === "text") previous.text += "\n";
      else segments.push(textSegment("\n"));
    } else if (inline?.type === "variable" && typeof inline?.attrs?.key === "string") {
      segments.push(variableSegment(inline.attrs.key, marksOfNode(inline)));
    }
  }
  return { id: nextId("p"), segments: ensureEditable(segments) };
}

function emptyParagraph() {
  return { id: nextId("p"), segments: [textSegment("")] };
}

function structuralBlock(node) {
  return { id: nextId("b"), type: STRUCTURAL, node };
}

function blockFromNode(node, structuralBlocks) {
  if (structuralBlocks.includes(node?.type)) return structuralBlock(node.type);
  if (node?.type === "bullet_list" || node?.type === "ordered_list") {
    const items = (Array.isArray(node.content) ? node.content : []).map((item) => ({
      id: nextId("i"),
      paragraphs: (Array.isArray(item?.content) && item.content.length
        ? item.content
        : [{ type: "paragraph" }]
      ).map(paragraphFromNode),
    }));
    return {
      id: nextId("b"),
      type: node.type,
      items: items.length ? items : [{ id: nextId("i"), paragraphs: [emptyParagraph()] }],
    };
  }
  return { id: nextId("b"), type: "paragraph", paragraph: paragraphFromNode(node) };
}

/** The structural block types the server named, as a clean list. */
function structuralOf(options) {
  const list = options?.structuralBlocks;
  return Array.isArray(list) ? list.filter((type) => typeof type === "string" && type) : [];
}

/**
 * The editor's body for a stored `body_doc`; an empty body for none.
 * `structuralBlocks`: the node types the server says are structural here.
 */
export function bodyFromDoc(doc, options = {}) {
  const structural = structuralOf(options);
  const content = Array.isArray(doc?.content) ? doc.content : [];
  const blocks = content.map((node) => blockFromNode(node, structural));
  return { blocks: blocks.length ? blocks : [paragraphBlock()] };
}

/** The editor's subject for a stored `subject_doc`; an empty one for none. */
export function subjectFromDoc(doc) {
  const first = Array.isArray(doc?.content) ? doc.content[0] : null;
  return first ? paragraphFromNode(first) : emptyParagraph();
}

/**
 * A new draft. Without structural blocks one empty paragraph (D10); with
 * them, each one between an empty paragraph before and after it — text to
 * write above the list and below it, and a body that already holds what the
 * server requires.
 */
export function emptyDraft(options = {}) {
  const structural = structuralOf(options);
  const blocks = structural.length
    ? [paragraphBlock(), ...structural.map(structuralBlock), paragraphBlock()]
    : [paragraphBlock()];
  return { subject: emptyParagraph(), body: { blocks } };
}

export function draftFromDocs(subjectDoc, bodyDoc, options = {}) {
  return { subject: subjectFromDoc(subjectDoc), body: bodyFromDoc(bodyDoc, options) };
}

// ── model → doc ─────────────────────────────────────────────────────────

function withMarks(node, marks) {
  const canonical = canonicalMarks(marks);
  return canonical.length ? { ...node, marks: canonical.map((type) => ({ type })) } : node;
}

function inlinesOf(paragraph, { subject }) {
  const nodes = [];
  for (const segment of mergeTexts(paragraph.segments)) {
    if (segment.kind === "variable") {
      nodes.push(
        subject
          ? { type: "variable", attrs: { key: segment.key } }
          : withMarks({ type: "variable", attrs: { key: segment.key } }, segment.marks)
      );
      continue;
    }
    const text = sanitizeText(segment.text, { singleLine: subject });
    const lines = text.split("\n");
    lines.forEach((line, index) => {
      if (line) {
        nodes.push(
          subject ? { type: "text", text: line } : withMarks({ type: "text", text: line }, segment.marks)
        );
      }
      if (index < lines.length - 1 && !subject) nodes.push({ type: "hard_break" });
    });
  }
  // Neighbouring texts with the same marks can meet once empty segments and
  // breaks are resolved; join them so equal letters serialise equally.
  const joined = [];
  for (const node of nodes) {
    const previous = joined[joined.length - 1];
    if (
      node.type === "text" &&
      previous &&
      previous.type === "text" &&
      JSON.stringify(previous.marks || []) === JSON.stringify(node.marks || [])
    ) {
      joined[joined.length - 1] = { ...previous, text: previous.text + node.text };
    } else {
      joined.push(node);
    }
  }
  return joined;
}

function paragraphNode(paragraph, { subject = false } = {}) {
  const content = inlinesOf(paragraph, { subject });
  return content.length ? { type: "paragraph", content } : { type: "paragraph" };
}

function blockNode(block) {
  if (block.type === STRUCTURAL) return { type: block.node };
  if (block.type === "paragraph") return paragraphNode(block.paragraph);
  return {
    type: block.type,
    content: block.items.map((item) => ({
      type: "list_item",
      content: item.paragraphs.map((paragraph) => paragraphNode(paragraph)),
    })),
  };
}

export function bodyToDoc(body) {
  return { type: "doc", content: body.blocks.map(blockNode) };
}

export function subjectToDoc(subject) {
  return { type: "doc", content: [paragraphNode(subject, { subject: true })] };
}

export function draftToDocs(draft) {
  return { subject_doc: subjectToDoc(draft.subject), body_doc: bodyToDoc(draft.body) };
}

/** One string per letter: what "dirty" and "current preview" compare. */
export function serializeDraft(draft) {
  return JSON.stringify(draftToDocs(draft));
}

// ── paragraph edits (pure; each returns a new paragraph) ────────────────

export function updateSegmentText(paragraph, segmentId, text) {
  return {
    ...paragraph,
    segments: paragraph.segments.map((segment) =>
      segment.id === segmentId && segment.kind === "text" ? { ...segment, text } : segment
    ),
  };
}

/**
 * Insert a placeholder into a text segment at `caret` (a selection is
 * replaced). Returns the paragraph and the id of the text after the
 * placeholder, where the caret belongs next.
 */
export function insertVariable(paragraph, segmentId, caret, key, { selectionEnd, inheritMarks = true } = {}) {
  const index = paragraph.segments.findIndex((s) => s.id === segmentId && s.kind === "text");
  if (index < 0) return { paragraph, caretSegmentId: null };
  const target = paragraph.segments[index];
  const start = Math.max(0, Math.min(caret ?? target.text.length, target.text.length));
  const end = Math.max(start, Math.min(selectionEnd ?? start, target.text.length));
  const before = { ...target, text: target.text.slice(0, start) };
  const after = textSegment(target.text.slice(end), target.marks);
  const variable = variableSegment(key, inheritMarks ? target.marks : []);
  const segments = [
    ...paragraph.segments.slice(0, index),
    before,
    variable,
    after,
    ...paragraph.segments.slice(index + 1),
  ];
  return { paragraph: { ...paragraph, segments: ensureEditable(segments) }, caretSegmentId: after.id };
}

/**
 * Remove one placeholder; the texts around it are joined when they carry the
 * same marks. Returns the paragraph and where the caret goes: the text that
 * now holds the join, at the join.
 */
export function removeVariable(paragraph, segmentId) {
  const index = paragraph.segments.findIndex((s) => s.id === segmentId && s.kind === "variable");
  if (index < 0) return { paragraph, caret: null };
  const before = paragraph.segments[index - 1];
  const after = paragraph.segments[index + 1];
  const rest = paragraph.segments.filter((_, i) => i !== index);
  let caret = null;
  if (before && before.kind === "text") caret = { segmentId: before.id, offset: before.text.length };
  else if (after && after.kind === "text") caret = { segmentId: after.id, offset: 0 };
  const merged = ensureEditable(mergeTexts(rest));
  if (caret && !merged.some((s) => s.id === caret.segmentId)) {
    caret = null;
  }
  return { paragraph: { ...paragraph, segments: merged }, caret };
}

/** The marks the text segment carries (`[]` for none or for a placeholder). */
export function marksOf(paragraph, segmentId) {
  const segment = paragraph.segments.find((s) => s.id === segmentId);
  return segment && segment.kind === "text" ? segment.marks : [];
}

/**
 * Toggle one mark on the selected part of a text segment, or on the whole
 * segment when nothing is selected. The selection becomes its own segment;
 * neighbours that end up with equal marks are joined again.
 */
export function toggleMark(paragraph, segmentId, start, end, mark) {
  if (!MARKS.includes(mark)) return paragraph;
  const index = paragraph.segments.findIndex((s) => s.id === segmentId && s.kind === "text");
  if (index < 0) return paragraph;
  const target = paragraph.segments[index];
  const from = Math.max(0, Math.min(start ?? 0, target.text.length));
  const to = Math.max(from, Math.min(end ?? from, target.text.length));
  const flip = (marks) =>
    marks.includes(mark) ? marks.filter((m) => m !== mark) : canonicalMarks([...marks, mark]);
  let pieces;
  if (from === to) {
    pieces = [{ ...target, marks: flip(target.marks) }];
  } else {
    pieces = [
      from > 0 ? { ...target, text: target.text.slice(0, from) } : null,
      { ...textSegment(target.text.slice(from, to), flip(target.marks)) },
      to < target.text.length ? textSegment(target.text.slice(to), target.marks) : null,
    ].filter(Boolean);
    if (from === 0) pieces[0] = { ...pieces[0], id: target.id };
  }
  const segments = [
    ...paragraph.segments.slice(0, index),
    ...pieces,
    ...paragraph.segments.slice(index + 1),
  ];
  return { ...paragraph, segments: ensureEditable(mergeTexts(segments)) };
}

/**
 * A caret position as one number across the paragraph (a placeholder counts
 * as one), so a selection can be found again after segments were split or
 * joined — the text and the placeholders' order do not change when marks do.
 */
export function absoluteOffset(paragraph, segmentId, offset) {
  let position = 0;
  for (const segment of paragraph.segments) {
    if (segment.id === segmentId) return position + (segment.kind === "text" ? offset : 0);
    position += segment.kind === "text" ? segment.text.length : 1;
  }
  return position;
}

/** The text segment that holds `[start, end)`, with offsets inside it. */
export function locateRange(paragraph, start, end) {
  let position = 0;
  let fallback = null;
  for (const segment of paragraph.segments) {
    const length = segment.kind === "text" ? segment.text.length : 1;
    if (segment.kind === "text") {
      if (start >= position && end <= position + length) {
        return { segmentId: segment.id, start: start - position, end: end - position };
      }
      if (!fallback && start >= position && start <= position + length) {
        fallback = { segmentId: segment.id, start: start - position, end: start - position };
      }
    }
    position += length;
  }
  return fallback;
}

// ── body edits ──────────────────────────────────────────────────────────

function paragraphBlock(paragraph = emptyParagraph()) {
  return { id: nextId("b"), type: "paragraph", paragraph };
}

function listBlock(type, paragraphs) {
  return {
    id: nextId("b"),
    type,
    items: (paragraphs.length ? paragraphs : [emptyParagraph()]).map((paragraph) => ({
      id: nextId("i"),
      paragraphs: [paragraph],
    })),
  };
}

/** Every paragraph of the body, in reading order. */
export function paragraphsOf(body) {
  const out = [];
  for (const block of body.blocks) {
    if (block.type === STRUCTURAL) continue;
    if (block.type === "paragraph") out.push(block.paragraph);
    else for (const item of block.items) out.push(...item.paragraphs);
  }
  return out;
}

/** Apply `fn` to the paragraph with this id, wherever it sits. */
export function mapParagraph(body, paragraphId, fn) {
  return {
    ...body,
    blocks: body.blocks.map((block) => {
      if (block.type === STRUCTURAL) return block;
      if (block.type === "paragraph") {
        return block.paragraph.id === paragraphId
          ? { ...block, paragraph: fn(block.paragraph) }
          : block;
      }
      return {
        ...block,
        items: block.items.map((item) => ({
          ...item,
          paragraphs: item.paragraphs.map((p) => (p.id === paragraphId ? fn(p) : p)),
        })),
      };
    }),
  };
}

export function addBlock(body, type = "paragraph") {
  const block = type === "paragraph" ? paragraphBlock() : listBlock(type, []);
  return { ...body, blocks: [...body.blocks, block] };
}

/**
 * A body keeps at least one block; the last one cannot be removed, and a
 * structural block never can — the server requires it.
 */
export function removeBlock(body, blockId) {
  if (body.blocks.length <= 1) return body;
  const target = body.blocks.find((block) => block.id === blockId);
  if (target && target.type === STRUCTURAL) return body;
  return { ...body, blocks: body.blocks.filter((block) => block.id !== blockId) };
}

/**
 * Turn a block into another type, keeping its text: a paragraph becomes a
 * one-point list, a list's points become paragraphs, and a list switches
 * between bullets and numbers as it is.
 */
export function setBlockType(body, blockId, type) {
  if (!BLOCK_TYPES.includes(type)) return body;
  const blocks = [];
  for (const block of body.blocks) {
    if (block.id !== blockId || block.type === type || block.type === STRUCTURAL) {
      blocks.push(block);
    } else if (type === "paragraph") {
      for (const item of block.items) {
        for (const paragraph of item.paragraphs) blocks.push(paragraphBlock(paragraph));
      }
    } else if (block.type === "paragraph") {
      blocks.push({ ...listBlock(type, [block.paragraph]), id: block.id });
    } else {
      blocks.push({ ...block, type });
    }
  }
  return { ...body, blocks };
}

export function addListItem(body, blockId) {
  return {
    ...body,
    blocks: body.blocks.map((block) =>
      block.id === blockId && LIST_TYPES.includes(block.type)
        ? { ...block, items: [...block.items, { id: nextId("i"), paragraphs: [emptyParagraph()] }] }
        : block
    ),
  };
}

/** A list keeps at least one point. */
export function removeListItem(body, blockId, itemId) {
  return {
    ...body,
    blocks: body.blocks.map((block) => {
      if (block.id !== blockId || !LIST_TYPES.includes(block.type) || block.items.length <= 1) {
        return block;
      }
      return { ...block, items: block.items.filter((item) => item.id !== itemId) };
    }),
  };
}

// ── checks against the server's limits ────────────────────────────────

function hasContentInline(paragraph) {
  return paragraph.segments.some(
    (segment) =>
      segment.kind === "variable" || (segment.kind === "text" && segment.text.trim() !== "")
  );
}

export function subjectHasContent(subject) {
  return hasContentInline(subject);
}

/** A structural block is content: the server writes real text into it. */
export function bodyHasContent(body) {
  return (
    body.blocks.some((block) => block.type === STRUCTURAL) ||
    paragraphsOf(body).some(hasContentInline)
  );
}

/** The subject's own text, in characters — the server counts the same. */
export function subjectTextLength(subject) {
  return inlinesOf(subject, { subject: true })
    .filter((node) => node.type === "text")
    .reduce((sum, node) => sum + node.text.length, 0);
}

function countNodes(node) {
  if (!node || typeof node !== "object") return 0;
  const children = Array.isArray(node.content) ? node.content : [];
  return 1 + children.reduce((sum, child) => sum + countNodes(child), 0);
}

/** UTF-8 length of the serialised document — what the server measures. */
function documentBytes(doc) {
  let bytes = 0;
  for (const ch of JSON.stringify(doc)) {
    const code = ch.codePointAt(0);
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/**
 * What the draft breaks before any request: each problem as a stable code the
 * labels module words. Limits are the server's (`limits` of the template), and
 * so are the structural blocks the body must hold exactly once.
 */
export function draftProblems(draft, limits = {}, options = {}) {
  const problems = [];
  for (const type of structuralOf(options)) {
    const count = draft.body.blocks.filter((b) => b.type === STRUCTURAL && b.node === type).length;
    if (count === 0 && !problems.includes("structural_block_missing")) {
      problems.push("structural_block_missing");
    }
    if (count > 1 && !problems.includes("structural_block_duplicate")) {
      problems.push("structural_block_duplicate");
    }
  }
  const docs = draftToDocs(draft);
  if (!subjectHasContent(draft.subject)) problems.push("subject_empty");
  if (limits.max_subject_chars && subjectTextLength(draft.subject) > limits.max_subject_chars) {
    problems.push("subject_too_long");
  }
  if (!bodyHasContent(draft.body)) problems.push("body_empty");
  if (limits.max_body_nodes && countNodes(docs.body_doc) > limits.max_body_nodes) {
    problems.push("too_many_nodes");
  }
  if (
    limits.max_document_bytes &&
    (documentBytes(docs.subject_doc) > limits.max_document_bytes ||
      documentBytes(docs.body_doc) > limits.max_document_bytes)
  ) {
    problems.push("too_large");
  }
  return problems;
}
